import { AxnReferenceDataTypes } from "@enums/axn-reference-data-types";
import { GenericReferenceDataTypes } from "@enums/generic-reference-data-types";
import { ReferenceDataTypes } from "@enums/reference-data-types";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { EmailTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/email-template";
import { MessagingTemplateConfiguration } from "@idw-types/data/messaging-template";
import {
    AxnAssertionStatusMap,
    AxnAttributeLabel,
    AxnAttributeType,
    AxnAttributeTypeField,
    AxnAttributeTypeFieldCustomization,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnCountry,
    AxnDocumentationLink,
    AxnIdentityProvider,
    AxnPropertyType,
    AxnRPServiceWithSOID,
    AxnRelyingParty,
    AxnSelectedServiceOfferingSO,
    AxnServiceOfferingDataSource,
    AxnTransactionType,
    SimpleAxnRpServiceWorkflow,
    SimpleAxnWorkflow
} from "@idw-types/data/reference-data/axn";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";
import {
    ApAssertion,
    Assertion,
    AssertionAttribute,
    AssertionScoreAssertionStatusValueMap,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    Auth0IdentityProvider,
    AvailableAssertionStatus,
    AvailableScoreValue,
    AvailableTrustScoreValue,
    AxnAdminUserPropertyCategory,
    ClaimStatus,
    DeployedPolicyComponent,
    FallbackServiceMapping,
    PolicyDecision,
    RiskLevel,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    RpServiceTemplate,
    RpServiceType,
    ScoreAttributeMap,
    SelfServiceVerificationAttribute,
    ServiceOffering,
    ServiceOfferingRefreshRate,
    SimpleDeployedPolicyComponentCondition,
    TrustScoreAssertionStatusValueMap,
    WorkflowRedirectURL,
    WorkflowTemplate
} from "@idw-types/data/reference-data/generic";
import { CustomerSupportTier } from "@idw-types/data/reference-data/generic/customer-support-tier";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { CommonReferenceData } from "@idw-types/state/common-reference-data-state";

//relying parties
export type RelyingPartiesReferenceData = {
    axnCompanies: Array<AxnCompany>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const RELYING_PARTIES_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//organizations
export type OrganizationsReferenceData = {
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    customerSupportTiers: Array<CustomerSupportTier>;

};

export const ORGANIZATIONS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    GenericReferenceDataTypes.AUTH0_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.AXN_ADMIN_USER_PROPERTY_BUNDLE,
    GenericReferenceDataTypes.CUSTOMER_SUPPORT_TIER
];

//organization activity
export type OrganizationActivityReferenceData = {
    axnCompanies: Array<AxnCompany>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const ORGANIZATION_ACTIVITY_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//organization invitations
export type OrganizationInvitationsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const ORGANIZATION_INVITATIONS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    AxnReferenceDataTypes.AXN_ADMIN_USER_PROPERTY_BUNDLE,
    GenericReferenceDataTypes.AUTH0_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//organization invitation details
export type OrganizationInvitationDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
};

export const ORGANIZATION_INVITATION_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    AxnReferenceDataTypes.AXN_ADMIN_USER_PROPERTY_BUNDLE
];

//organization users
export type OrganizationUsersReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAdminUserPropertyCategories: Array<AxnAdminUserPropertyCategory>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    documentationLinks: Array<AxnDocumentationLink>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>;
};

export const ORGANIZATION_USERS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    GenericReferenceDataTypes.AXNADMIN_USER_PROPERTY_CATEGORY,
    AxnReferenceDataTypes.AXN_ADMIN_USER_PROPERTY_BUNDLE,
    GenericReferenceDataTypes.AUTH0_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.DOCUMENTATION_LINK,
    AxnReferenceDataTypes.AXN_ADMIN_AVAILABLE_USER_RP_PROPERTY,
    AxnReferenceDataTypes.AXN_ADMIN_AVAILABLE_USER_ORGANIZATION_PROPERTY
];

//organization user details
export type OrganizationUserDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAdminUserPropertyCategories: Array<AxnAdminUserPropertyCategory>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const ORGANIZATION_USER_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    GenericReferenceDataTypes.AXNADMIN_USER_PROPERTY_CATEGORY,
    AxnReferenceDataTypes.AXN_ADMIN_USER_PROPERTY_BUNDLE,
    AxnReferenceDataTypes.AXN_ADMIN_AVAILABLE_USER_RP_PROPERTY,
    AxnReferenceDataTypes.AXN_ADMIN_AVAILABLE_USER_ORGANIZATION_PROPERTY,
    GenericReferenceDataTypes.AUTH0_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//organization user details
export type OrganizationDetailsReferenceData = {
    axnIdentityProviders: Array<AxnIdentityProvider>;
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    customerSupportTiers: Array<CustomerSupportTier>;
};

export const ORGANIZATION_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER,
    GenericReferenceDataTypes.AUTH0_IDENTITY_PROVIDER,
    GenericReferenceDataTypes.CUSTOMER_SUPPORT_TIER
];

//attribute labels
export type AttributeLabelsReferenceData = {
    axnAttributeTypes: Array<AxnAttributeType>;
};

export const ATTRIBUTE_LABELS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE
];

//attribute label details
export type AttributeLabelDetailsReferenceData = {
    axnAttributeTypes: Array<AxnAttributeType>;
};

export const ATTRIBUTE_LABEL_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE
];

//dashboard
export type DashboardReferenceData = {
    axnCompanies: Array<AxnCompany>;
    rpServiceTypes: Array<RpServiceType>;
};

export const DASHBOARD_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    GenericReferenceDataTypes.RP_SERVICE_TYPE
];

//workflow report
export type WorkflowReportReferenceData = {
    axnAssertions: Array<Assertion>;
    axnCountries: Array<AxnCountry>;
    axnRelyingParties: Array<AxnRelyingParty>;
    riskLevels: Array<RiskLevel>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const WORKFLOW_REPORT_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_ASSERTION,
    AxnReferenceDataTypes.AXN_COUNTRY,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    GenericReferenceDataTypes.RISK_LEVEL,
    AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//verification transactions
export type VerificationTransactionsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnAssertions: Array<Assertion>;
    axnCountries: Array<AxnCountry>;
    axnRelyingParties: Array<AxnRelyingParty>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    simpleAXNRPServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>;
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>;
    axnRPServicesWithSOIDs: Array<AxnRPServiceWithSOID>;
    claimStatuses: Array<ClaimStatus>;
    policyDecisions: Array<PolicyDecision>;
    apAssertions: Array<ApAssertion>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const VERIFICATION_TRANSACTIONS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_ASSERTION,
    AxnReferenceDataTypes.AXN_COUNTRY,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW,
    AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE_WORKFLOW,
    AxnReferenceDataTypes.AXN_SELECTED_SERVICE_OFFERING_SO,
    AxnReferenceDataTypes.AXN_RP_SERVICE_WITH_SOIDS,
    GenericReferenceDataTypes.CLAIM_STATUS,
    GenericReferenceDataTypes.POLICY_DECISION,
    AxnReferenceDataTypes.AXN_AP_ASSERTION,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//policy components
export type PolicyComponentsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>;
    axnServiceOfferings: Array<ServiceOffering>;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    apAssertions: Array<ApAssertion>;
    axnAssertions: Array<Assertion>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const POLICY_COMPONENTS_REFERENCE_DATA_TYPES = [
    AxnReferenceDataTypes.AXN_COMPANY,
    GenericReferenceDataTypes.SIMPLE_DEPLOYED_POLICY_COMPONENT_CONDITION,
    AxnReferenceDataTypes.AXN_SERVICE_OFFERING,
    AxnReferenceDataTypes.AXN_ACQUIRED_ATTRIBUTE,
    AxnReferenceDataTypes.AXN_AP_ASSERTION,
    AxnReferenceDataTypes.AXN_ASSERTION,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE,
    AxnReferenceDataTypes.AXN_AVAILABLE_ASSERTION_STATUS,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//policy component details
export type PolicyComponentDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    deployedPolicyComponents: Array<DeployedPolicyComponent>;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    axnAssertions: Array<Assertion>;
    axnServiceOfferings: Array<ServiceOffering>;
    apAssertions: Array<ApAssertion>;
    axnRelyingParties: Array<AxnRelyingParty>;
    simpleAXNRPServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    simpleAXNRPServices: Array<RpServiceConfiguration>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
};

export const POLICY_COMPONENT_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD,
    GenericReferenceDataTypes.DEPLOYED_POLICY_COMPONENT,
    AxnReferenceDataTypes.AXN_ACQUIRED_ATTRIBUTE,
    AxnReferenceDataTypes.AXN_ASSERTION,
    AxnReferenceDataTypes.AXN_SERVICE_OFFERING,
    AxnReferenceDataTypes.AXN_AP_ASSERTION,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE_WORKFLOW,
    AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW,
    AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE,
    AxnReferenceDataTypes.AXN_AVAILABLE_ASSERTION_STATUS
];

//relying party details
export type RelyingPartyDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const RELYING_PARTY_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//workflow templates
export type WorkflowTemplatesReferenceData = {
    axnCompanies: Array<AxnCompany>;
    deployedPolicyComponents: Array<DeployedPolicyComponent>;
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>;
};

export const WORKFLOW_TEMPLATES_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    GenericReferenceDataTypes.DEPLOYED_POLICY_COMPONENT,
    GenericReferenceDataTypes.SIMPLE_DEPLOYED_POLICY_COMPONENT_CONDITION
];

//service templates
export type ServiceTemplatesReferenceData = {
    axnCompanies: Array<AxnCompany>;
};

export const SERVICE_TEMPLATES_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY
];

export type ServiceTemplateDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    rpServiceTemplates: Array<RpServiceTemplate>;
};

export const SERVICE_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    GenericReferenceDataTypes.RP_SERVICE_TEMPLATE
];

export type VerificationServicesReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnCountries: Array<AxnCountry>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnRelyingParties: Array<AxnRelyingParty>;
    selfServiceVerificationAttributes: Array<SelfServiceVerificationAttribute>;
    selfServiceVerificationEmailTemplates: Array<EmailTemplate>;
    rpServiceTemplates: Array<RpServiceTemplate>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    rpServiceTypes: Array<RpServiceType>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    axnAdminWorkflows: Array<SimpleAxnWorkflow>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    documentationLinks: Array<AxnDocumentationLink>;
    axnAttributeTypes: Array<AttributeType>;
    axnAttributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
};

export const VERIFICATION_SERVICES_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_COUNTRY,
    AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_ATTRIBUTE,
    GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_EMAIL_TEMPLATE,
    GenericReferenceDataTypes.RP_SERVICE_TEMPLATE,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD_CUSTOMIZATION,
    GenericReferenceDataTypes.RP_SERVICE_TYPE,
    AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW,
    AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_WORKFLOW,
    AxnReferenceDataTypes.AXN_AVAILABLE_PROPERTY_CATEGORY,
    AxnReferenceDataTypes.DOCUMENTATION_LINK,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_CLASSIFICATION,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_LABEL,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE,
    GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY,
    GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY_MAP
];

export type WorkflowsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnCountries: Array<AxnCountry>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnRelyingParties: Array<AxnRelyingParty>;
    selfServiceVerificationAttributes: Array<SelfServiceVerificationAttribute>;
    selfServiceVerificationEmailTemplates: Array<EmailTemplate>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    workflowRedirectURLs: Array<WorkflowRedirectURL>;
    workflowTemplates: Array<WorkflowTemplate>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    documentationLinks: Array<AxnDocumentationLink>;
    axnAttributeTypes: Array<AttributeType>;
    axnAttributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
};

export const WORKFLOWS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_COUNTRY,
    AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_EMAIL_TEMPLATE,
    GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_ATTRIBUTE,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD_CUSTOMIZATION,
    GenericReferenceDataTypes.WORKFLOW_REDIRECT_URL,
    GenericReferenceDataTypes.WORKFLOW_TEMPLATE,
    AxnReferenceDataTypes.AXN_AVAILABLE_PROPERTY_CATEGORY,
    AxnReferenceDataTypes.DOCUMENTATION_LINK,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_CLASSIFICATION,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_LABEL,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE,
    GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY,
    GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY_MAP
];

//verification service details
export type VerificationServiceDetailsReferenceData = {
    axnRPServices: Array<AxnRpService>;
    axnServiceOfferingDataSources: Array<AxnServiceOfferingDataSource>;
    fallbackServiceMappings: Array<FallbackServiceMapping>;
    deployedPolicyComponents: Array<DeployedPolicyComponent>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>;
    simpleAXNRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    simpleAXNAdminRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnCompanies: Array<AxnCompany>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    axnAdminWorkflows: Array<SimpleAxnWorkflow>;
    documentationLinks: Array<AxnDocumentationLink>;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    messagingTemplates: Array<MessagingTemplateConfiguration>;
    selfServiceVerificationEmailTemplates: Array<EmailTemplate>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    axnAttributeLabels: Array<AxnAttributeLabel>;
    axnAssertionAttributes: Array<AssertionAttribute>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAssertions: Array<Assertion>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnAttributeTypes: Array<AxnAttributeType>;
    attributeClassifications: Array<AttributeClassification>;
    apAssertions: Array<ApAssertion>;
    availableAssertionScoreValues: Array<AvailableScoreValue>;
    availableTrustScoreValues: Array<AvailableTrustScoreValue>;
    serviceOfferingRefreshRateDefault: string;
    serviceOfferingRefreshRates: Array<ServiceOfferingRefreshRate>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
    rpServiceTypes: Array<RpServiceType>;
    axnPropertyTypes: Array<AxnPropertyType>;
    assertionScoreServiceOfferings: Array<string>;
    trustScoreServiceOfferings: Array<string>;
    scoreAttributeMaps: Array<ScoreAttributeMap>;
    assertionScoreAssertionStatusValueMaps: Array<AssertionScoreAssertionStatusValueMap>;
    trustScoreAssertionStatusValueMaps: Array<TrustScoreAssertionStatusValueMap>;
    axnServiceOfferings: Array<ServiceOffering>;
    assertionStatusDefaults: Array<string>;
    axnAssertionStatusMaps: Array<AxnAssertionStatusMap>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
};

export const VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_SERVICE_OFFERING_DATA_SOURCE,
    GenericReferenceDataTypes.FALLBACK_SERVICE_MAPPING,
    GenericReferenceDataTypes.DEPLOYED_POLICY_COMPONENT,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD,
    GenericReferenceDataTypes.SIMPLE_DEPLOYED_POLICY_COMPONENT_CONDITION,
    AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_RP_SERVICE_WORKFLOW,
    AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE_WORKFLOW,
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW,
    AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_WORKFLOW,
    AxnReferenceDataTypes.DOCUMENTATION_LINK,
    AxnReferenceDataTypes.AXN_ACQUIRED_ATTRIBUTE,
    GenericReferenceDataTypes.MESSAGING_TEMPLATE,
    GenericReferenceDataTypes.EMAIL_TEMPLATE,
    AxnReferenceDataTypes.AXN_AVAILABLE_ASSERTION_STATUS,
    AxnReferenceDataTypes.AXN_AVAILABLE_PROPERTY_CATEGORY,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_LABEL,
    AxnReferenceDataTypes.AXN_ASSERTION_ATTRIBUTE,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    AxnReferenceDataTypes.AXN_ASSERTION,
    AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_CLASSIFICATION,
    AxnReferenceDataTypes.AXN_AP_ASSERTION,
    GenericReferenceDataTypes.AVAILABLE_ASSERTION_SCORE_VALUE,
    GenericReferenceDataTypes.AVAILABLE_TRUST_SCORE_VALUE,
    GenericReferenceDataTypes.SERVICE_OFFERING_REFRESH_RATE_DEFAULT,
    GenericReferenceDataTypes.SERVICE_OFFERING_REFRESH_RATE,
    GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY,
    GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY_MAP,
    GenericReferenceDataTypes.RP_SERVICE_TYPE,
    AxnReferenceDataTypes.AXN_PROPERTY_TYPE,
    GenericReferenceDataTypes.TRUST_SCORE_SERVICE_OFFERING,
    GenericReferenceDataTypes.ASSERTION_SCORE_SERVICE_OFFERING,
    GenericReferenceDataTypes.SCORE_ATTRIBUTE_MAP,
    GenericReferenceDataTypes.ASSERTION_SCORE_ASSERTION_STATUS_VALUE_MAP,
    GenericReferenceDataTypes.TRUST_SCORE_ASSERTION_STATUS_VALUE_MAP,
    AxnReferenceDataTypes.AXN_SERVICE_OFFERING,
    GenericReferenceDataTypes.ASSERTION_STATUS_DEFAULT,
    AxnReferenceDataTypes.AXN_ASSERTION_STATUS_MAP,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD_CUSTOMIZATION,
];

//workflow details
export type WorkflowDetailsReferenceData = { rpServiceTemplates: Array<RpServiceTemplate>; } & VerificationServiceDetailsReferenceData;

export const WORKFLOW_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    ...VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES,
    GenericReferenceDataTypes.RP_SERVICE_TEMPLATE

];

export const WORKFLOW_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    ...VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES,
    GenericReferenceDataTypes.RP_SERVICE_TEMPLATE
];

//verification transaction details
export type VerificationTransactionDetailsReferenceData = {
    axnAssertions: Array<Assertion>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnTransactionTypes: Array<AxnTransactionType>;
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>;
    axnCountries: Array<AxnCountry>;
    documentationLinks: Array<AxnDocumentationLink>;
};

export const VERIFICATION_TRANSACTION_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_ASSERTION,
    AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    AxnReferenceDataTypes.AXN_TRANSACTION_TYPE,
    AxnReferenceDataTypes.AXN_SELECTED_SERVICE_OFFERING_SO,
    AxnReferenceDataTypes.AXN_COUNTRY,
    AxnReferenceDataTypes.DOCUMENTATION_LINK
];

//workflow usage details
export type WorkflowUsageDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    simpleAXNRPServices: Array<RpServiceConfiguration>;
    axnServiceOfferings: Array<ServiceOffering>;
};

export const WORKFLOW_USAGE_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_RELYING_PARTY,
    AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE,
    AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW,
    AxnReferenceDataTypes.AXN_SERVICE_OFFERING
];

export type EmailTemplatesReferenceData = {
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
};

export const EMAIL_TEMPLATES_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_WHITE_LABELED_EMAILS,
];

//email template details
export type EmailTemplateDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
};

export const EMAIL_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_WHITE_LABELED_EMAILS,
];

//messaging templates
export type MessagingTemplatesReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
};

export const MESSAGING_TEMPLATES_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_RELYING_PARTY
];

//messaging template details
export type MessagingTemplateDetailsReferenceData = {
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
};

export const MESSAGING_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES: Array<ReferenceDataTypes> = [
    AxnReferenceDataTypes.AXN_COMPANY,
    AxnReferenceDataTypes.AXN_RELYING_PARTY
];

export const REFERENCE_DATA_PROPERTY_NAMES: { [key in ReferenceDataTypes]?: keyof CommonReferenceData } = {
    [AxnReferenceDataTypes.AXN_AP_ASSERTION]: "apAssertions",
    [GenericReferenceDataTypes.ASSERTION_SCORE_ASSERTION_STATUS_VALUE_MAP]: "assertionScoreAssertionStatusValueMaps",
    [GenericReferenceDataTypes.ASSERTION_SCORE_SERVICE_OFFERING]: "assertionScoreServiceOfferings",
    [GenericReferenceDataTypes.ASSERTION_STATUS_DEFAULT]: "assertionStatusDefaults",
    [AxnReferenceDataTypes.AXN_ATTRIBUTE_CLASSIFICATION]: "attributeClassifications",
    [GenericReferenceDataTypes.AUTH0_IDENTITY_PROVIDER]: "auth0IdentityProviders",
    [GenericReferenceDataTypes.AVAILABLE_ASSERTION_SCORE_VALUE]: "availableAssertionScoreValues",
    [GenericReferenceDataTypes.AVAILABLE_TRUST_SCORE_VALUE]: "availableTrustScoreValues",
    [AxnReferenceDataTypes.AXN_ASSERTION]: "axnAssertions",
    [AxnReferenceDataTypes.AXN_ACQUIRED_ATTRIBUTE]: "axnAcquiredAttributes",
    [AxnReferenceDataTypes.AXN_ADMIN_AVAILABLE_USER_ORGANIZATION_PROPERTY]: "axnAdminAvailableUserOrganizationProperties",
    [AxnReferenceDataTypes.AXN_ADMIN_AVAILABLE_USER_RP_PROPERTY]: "axnAdminAvailableUserRPProperties",
    [AxnReferenceDataTypes.AXN_ADMIN_USER_PROPERTY_BUNDLE]: "axnAdminUserPropertyBundles",
    [GenericReferenceDataTypes.AXNADMIN_USER_PROPERTY_CATEGORY]: "axnAdminUserPropertyCategories",
    [AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_WORKFLOW]: "axnAdminWorkflows",
    [AxnReferenceDataTypes.AXN_ASSERTION_ATTRIBUTE]: "axnAssertionAttributes",
    [AxnReferenceDataTypes.AXN_ASSERTION_STATUS_MAP]: "axnAssertionStatusMaps",
    [AxnReferenceDataTypes.AXN_ATTRIBUTE_LABEL]: "axnAttributeLabels",
    [AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE]: "axnAttributeTypes",
    [AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD]: "axnAttributeTypeFields",
    [AxnReferenceDataTypes.AXN_ATTRIBUTE_TYPE_FIELD_CUSTOMIZATION]: "axnAttributeTypeFieldCustomizations",
    [AxnReferenceDataTypes.AXN_AVAILABLE_ASSERTION_STATUS]: "axnAvailableAssertionStatuses",
    [AxnReferenceDataTypes.AXN_AVAILABLE_PROPERTY_CATEGORY]: "axnAvailablePropertyCategories",
    [AxnReferenceDataTypes.AXN_COMPANY]: "axnCompanies",
    [AxnReferenceDataTypes.AXN_COUNTRY]: "axnCountries",
    [AxnReferenceDataTypes.AXN_IDENTITY_PROVIDER]: "axnIdentityProviders",
    [AxnReferenceDataTypes.AXN_PROPERTY_TYPE]: "axnPropertyTypes",
    [AxnReferenceDataTypes.AXN_RELYING_PARTY]: "axnRelyingParties",
    [AxnReferenceDataTypes.AXN_RP_SERVICE_WITH_SOIDS]: "axnRPServicesWithSOIDs",
    [AxnReferenceDataTypes.AXN_SELECTED_SERVICE_OFFERING_SO]: "axnSelectedServiceOfferingSOs",
    [AxnReferenceDataTypes.AXN_SERVICE_OFFERING]: "axnServiceOfferings",
    [GenericReferenceDataTypes.SERVICE_OFFERING_CATEGORY]: "axnServiceOfferingCategories",
    [AxnReferenceDataTypes.AXN_SERVICE_OFFERING_DATA_SOURCE]: "axnServiceOfferingDataSources",
    [GenericReferenceDataTypes.SERVICE_OFFERING_PROCESSING_TYPE]: "axnServiceOfferingProcessingTypes",
    [AxnReferenceDataTypes.AXN_TRANSACTION_TYPE]: "axnTransactionTypes",
    [GenericReferenceDataTypes.CLAIM_STATUS]: "claimStatuses",
    [GenericReferenceDataTypes.CUSTOMER_SUPPORT_TIER]: "customerSupportTiers",
    [GenericReferenceDataTypes.DEPLOYED_POLICY_COMPONENT]: "deployedPolicyComponents",
    [AxnReferenceDataTypes.DOCUMENTATION_LINK]: "documentationLinks",
    [GenericReferenceDataTypes.EMAIL_TEMPLATE]: "emailTemplate",
    [GenericReferenceDataTypes.FALLBACK_SERVICE_MAPPING]: "fallbackServiceMappings",
    [GenericReferenceDataTypes.MESSAGING_TEMPLATE]: "messagingTemplates",
    [GenericReferenceDataTypes.POLICY_DECISION]: "policyDecisions",
    [GenericReferenceDataTypes.RISK_LEVEL]: "riskLevels",
    [GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY_MAP]: "rpServiceAttributePropertyMaps",
    [GenericReferenceDataTypes.RP_SERVICE_ATTRIBUTE_PROPERTY]: "rpServiceAttributeProperties",
    [GenericReferenceDataTypes.RP_SERVICE_TEMPLATE]: "rpServiceTemplates",
    [GenericReferenceDataTypes.RP_SERVICE_TYPE]: "rpServiceTypes",
    [GenericReferenceDataTypes.SCORE_ATTRIBUTE_MAP]: "scoreAttributeMaps",
    [GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_ATTRIBUTE]: "selfServiceVerificationAttributes",
    [GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_EMAIL_TEMPLATE]: "selfServiceVerificationEmailTemplates",
    [GenericReferenceDataTypes.SELF_SERVICE_VERIFICATION_WHITE_LABELED_EMAILS]: "selfServiceVerificationWhiteLabeledEmails",
    [GenericReferenceDataTypes.SERVICE_OFFERING_REFRESH_RATE_DEFAULT]: "serviceOfferingRefreshRateDefault",
    [GenericReferenceDataTypes.SERVICE_OFFERING_REFRESH_RATE]: "serviceOfferingRefreshRates",
    [AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW]: "simpleAXNWorkflows",
    [AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_RP_SERVICE_WORKFLOW]: "simpleAXNAdminRPServiceWorkflows",
    [AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE]: "simpleAXNRPServices",
    [AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE_WORKFLOW]: "simpleAXNRPServiceWorkflows",
    [GenericReferenceDataTypes.SIMPLE_DEPLOYED_POLICY_COMPONENT_CONDITION]: "simpleDeployedPolicyComponentConditions",
    [GenericReferenceDataTypes.SIMPLE_DEPLOYED_POLICY_CONFIG_DECISION]: "simpleDeployedPolicyConfigDecisions",
    [GenericReferenceDataTypes.SIMPLE_DEPLOYED_POLICY_CONFIG]: "simpleDeployedPolicyConfigs",
    [GenericReferenceDataTypes.TRUST_SCORE_ASSERTION_STATUS_VALUE_MAP]: "trustScoreAssertionStatusValueMaps",
    [GenericReferenceDataTypes.TRUST_SCORE_SERVICE_OFFERING]: "trustScoreServiceOfferings",
    [GenericReferenceDataTypes.WORKFLOW_REDIRECT_URL]: "workflowRedirectURLs",
    [GenericReferenceDataTypes.WORKFLOW_TEMPLATE]: "workflowTemplates"
} as const;
